import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import formStyles from 'pages/Form.module.css';
import globalStyles from 'Global.module.css';
import { UpdatePersonDocument, CreateImageUploadDocument } from 'graphql/mutations.generated';
import { GetPersonDetailDocument, GetPersonDetailQuery } from 'graphql/queries.generated';
import ImageUpload from 'components/ImageUpload';
import { ImageOwnerType, ImageSize } from 'app-constants';
import { useUser } from 'contexts/UserContext';

const EditProfile: React.FC = () => {
    const [username, setUsername] = useState('');
    const [bio, setBio] = useState('');
    const [region, setRegion] = useState('');
    const [error, setError] = useState('');
    const [pendingImageUpload, setPendingImageUpload] = useState<{
        uploadInput: any;
        file: File;
    } | null>(null);
    const navigate = useNavigate();
    const { user } = useUser();

    const { data, loading: queryLoading } = useQuery<GetPersonDetailQuery>(GetPersonDetailDocument, {
        variables: {
            personId: user?.personId || ''
        },
        skip: !user?.personId
    });

    const [updatePerson, { loading: updateLoading }] = useMutation(UpdatePersonDocument);
    const [createImageUpload] = useMutation(CreateImageUploadDocument);

    const loading = updateLoading;

    useEffect(() => {
        if (data?.person) {
            const person = data.person;
            setUsername(person.profile?.username || '');
            setBio(person.profile?.bio || '');
            setRegion(person.region);
        }
    }, [data]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!user?.personId) {
            setError('User not logged in');
            return;
        }

        try {
            let imageIdToUse = data?.person?.profile?.imageId;

            // Handle image upload if pending
            if (pendingImageUpload) {
                const uploadResult = await createImageUpload({
                    variables: {
                        ownerId: pendingImageUpload.uploadInput.ownerId,
                        ownerType: pendingImageUpload.uploadInput.ownerType,
                        contentType: pendingImageUpload.uploadInput.contentType,
                        crop_left: pendingImageUpload.uploadInput.crop_left,
                        crop_top: pendingImageUpload.uploadInput.crop_top,
                        crop_width: pendingImageUpload.uploadInput.crop_width,
                        crop_height: pendingImageUpload.uploadInput.crop_height,
                    },
                });

                if (uploadResult.data?.createImageUpload) {
                    const { imageId, uploadURL } = uploadResult.data.createImageUpload;

                    // Upload the file to S3
                    await fetch(uploadURL, {
                        method: 'PUT',
                        body: pendingImageUpload.file,
                    });

                    imageIdToUse = imageId;
                }
            }

            const result = await updatePerson({
                variables: {
                    personId: user.personId,
                    username,
                    bio,
                    region,
                    imageId: imageIdToUse,
                },
            });

            if (result.data?.updatePerson) {
                navigate('/account-settings');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    if (queryLoading) {
        return (
            <div className={`${formStyles.formContainer}`}>
                <p>Loading profile...</p>
            </div>
        );
    }

    if (!user?.personId) {
        return (
            <div className={`${formStyles.formContainer}`}>
                <p className={formStyles.error}>Please log in to edit your profile</p>
            </div>
        );
    }

    return (
        <div className={`${formStyles.formContainer}`}>
            <h1>Edit Profile</h1>
            <form onSubmit={handleSubmit}>
                <div>
                    <label htmlFor="username">Username:</label>
                    <input 
                        type="text" 
                        id="username" 
                        value={username} 
                        onChange={(e) => setUsername(e.target.value)} 
                        required 
                    />
                </div>
                <div>
                    <label htmlFor="bio">Bio:</label>
                    <textarea
                        id="bio"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={4}
                    />
                </div>
                <div>
                    <label htmlFor="region">Region:</label>
                    <input
                        type="text"
                        id="region"
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <label>Profile Image:</label>
                    <ImageUpload
                        ownerType={ImageOwnerType.Person}
                        displaySize={ImageSize.Profile}
                        imageId={data?.person?.profile?.imageId}
                        ownerId={user.personId}
                        onImageChange={setPendingImageUpload}
                        aspectRatio={1}
                    />
                </div>
                {error && <div className={formStyles.error}>{error}</div>}
                <button type="submit" disabled={loading} className={globalStyles.submitButton}>
                    {loading ? 'Updating profile...' : 'Update Profile'}
                </button>
            </form>
        </div>
    );
};

export default EditProfile;